import type { CreateSOPFormDTO } from "@/modules/sop/domain/contracts";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormMultiCombobox } from "@/shared/ui/FormMultiCombobox";
import { SmartFileUpload } from "@/shared/ui/SmartFileUpload";
import { Button, FormInput, FormTextarea, type RHF } from "@devhop/ui";
import { FileIcon, PlusIcon, TrashIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { SOPStepsList } from "./SOPStepsList";

type DepartmentItem = { id: string; name: string };
type PositionItem = { id: string; name: string };
type UserItem = { id: string; name: string };

interface SOPFormSectionsProps {
  formMethods: RHF.UseFormReturn<CreateSOPFormDTO>;
}

export function SOPFormSections({ formMethods }: SOPFormSectionsProps) {
  const steps = formMethods.watch("steps") ?? [];
  const documents = formMethods.watch("documents") ?? [];
  const warnings = formMethods.watch("warnings") ?? [];
  const departmentId = formMethods.watch("departmentId") as string | undefined;
  const positionIds = (formMethods.watch("positionIds") ?? []) as string[];

  const handleStepsChange = (newSteps: typeof steps) => {
    formMethods.setValue("steps", newSteps);
  };

  const handleAddDocument = () => {
    const newDoc = {
      id: nanoid(),
      title: "",
      fileKey: null,
    };
    formMethods.setValue("documents", [...documents, newDoc]);
  };

  const handleUpdateDocument = (
    index: number,
    updates: Partial<(typeof documents)[0]>,
  ) => {
    const newDocs = [...documents];
    newDocs[index] = { ...newDocs[index], ...updates };
    formMethods.setValue("documents", newDocs);
  };

  const handleDeleteDocument = (index: number) => {
    formMethods.setValue(
      "documents",
      documents.filter((_, i) => i !== index),
    );
  };

  const handleAddWarning = () => {
    formMethods.setValue("warnings", [...warnings, ""]);
  };

  const handleUpdateWarning = (index: number, value: string) => {
    const newWarnings = [...warnings];
    newWarnings[index] = value;
    formMethods.setValue("warnings", newWarnings);
  };

  const handleDeleteWarning = (index: number) => {
    formMethods.setValue(
      "warnings",
      warnings.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="space-y-8">
      {/* Section 1: ข้อมูล SOP */}
      <div className="space-y-4">
        <h2 className="font-semibold text-lg">ສ່ວນທີ 1: ຂໍ້ມູນ SOP</h2>
        <div className="grid grid-cols-2 gap-4 rounded-lg border bg-card p-4">
          <FormInput
            name="title"
            label="ຊື່ SOP"
            requiredMark
            placeholder="ຕົວຢ່າງ: ຂັ້ນຕອນການຈັດການລູກຄ້າ"
          />

          <FormInfiniteCombobox<DepartmentItem>
            name="departmentId"
            label="ເລືອກພະແນກ"
            queryKey={["departments-lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ..."
            clearable
          />

          <FormMultiCombobox<PositionItem>
            name="positionIds"
            label="ເລືອກວຽກທີ່ກ່ຽວຂ້ອງ"
            queryKey={["positions-lookup", departmentId ?? ""]}
            queryFn={(args) => {
              const base =
                `${config.apiUrl}/positions/lookup` +
                (departmentId
                  ? `?departmentId=${encodeURIComponent(departmentId)}`
                  : "");
              return fetchLookupForInfinite(base, args);
            }}
            preloadQueryFn={(ids) =>
              Promise.all(
                ids.map((id) =>
                  hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id),
                ),
              ).then((items) =>
                items.filter((item): item is PositionItem => item !== null),
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກວຽກ..."
            clearable
          />

          <FormInfiniteCombobox<UserItem>
            name="responsiblePersonId"
            label="ເລືອກຜູ້ຮັບຜິດຊອບ"
            queryKey={["users-lookup", positionIds.join(",")]}
            queryFn={(args) => {
              const base = `${config.apiUrl}/users/lookup`;
              return fetchLookupForInfinite(base, args);
            }}
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຜູ້ຮັບຜິດຊອບ..."
            clearable
          />
        </div>
      </div>

      {/* Section 2: จุดประสงค์ & ขอบเขต */}
      <div className="space-y-4">
        <h2 className="font-semibold text-lg">ສ່ວນທີ 2: ຈຸດປະສົງ & ຂອບເຂດ</h2>
        <div className="space-y-4 rounded-lg border bg-card p-4">
          <FormTextarea
            name="purpose"
            label="ຈຸດປະສົງ"
            placeholder="ຕົວຢ່າງ: ເພື່ອກໍານົດຂັ້ນຕອນການເຮັດວຽກທີ່ມາດຕະຖານ..."
            rows={4}
          />

          <FormTextarea
            name="scope"
            label="ຂອບເຂດ"
            placeholder="ຕົວຢ່າງ: SOP ນີ້ໃຊ້ສໍາລັບທຸກພະແນກ..."
            rows={4}
          />
        </div>
      </div>

      {/* Section 3: ขั้นตอนการทำงาน */}
      <div className="space-y-4">
        <h2 className="font-semibold text-lg">ສ່ວນທີ 3: ຂັ້ນຕອນການເຮັດວຽກ</h2>
        <div className="rounded-lg border bg-card p-4">
          <SOPStepsList
            steps={steps}
            onChange={handleStepsChange}
            formMethods={formMethods as never}
          />
        </div>
      </div>

      {/* Section 4: เอกสารแนบ */}
      <div className="space-y-4">
        <h2 className="font-semibold text-lg">ສ່ວນທີ 4: ເອກະສານແນບ</h2>
        <div className="space-y-4 rounded-lg border bg-card p-4">
          {documents.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີເອກະສານ ກົດປຸ່ມ "ເພີ່ມເອກະສານ" ເພື່ອເລີ່ມຕົ້ນ
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc, index) => (
                <div
                  key={doc.id}
                  className="flex items-start gap-3 rounded-lg border p-3"
                >
                  <FileIcon className="mt-1 h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      placeholder="ຊື່ເອກະສານ..."
                      value={doc.title}
                      onChange={(e) =>
                        handleUpdateDocument(index, { title: e.target.value })
                      }
                      className="w-full rounded-md border px-3 py-2 text-sm"
                    />
                    <SmartFileUpload
                      keyPrefix="uploads/sop"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf"
                      onSuccess={(key) =>
                        handleUpdateDocument(index, {
                          fileKey: key,
                          title: doc.title || key.split("/").pop() || "",
                        })
                      }
                      buttonLabel="ອັບໂຫຼດເອກະສານ"
                    />
                    {doc.fileKey && (
                      <div className="text-muted-foreground text-sm">
                        ອັບໂຫຼດແລ້ວ: {doc.fileKey.split("/").pop()}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteDocument(index)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddDocument}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            ເພີ່ມເອກະສານ
          </Button>
        </div>
      </div>

      {/* Section 5: ข้อควรระวัง */}
      <div className="space-y-4">
        <h2 className="font-semibold text-lg">ສ່ວນທີ 5: ຂໍ້ຄວນລະວັງ</h2>
        <div className="space-y-4 rounded-lg border bg-card p-4">
          {warnings.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
              ຍັງບໍ່ມີຂໍ້ຄວນລະວັງ ກົດປຸ່ມ "ເພີ່ມຂໍ້ຄວນລະວັງ" ເພື່ອເລີ່ມຕົ້ນ
            </div>
          ) : (
            <div className="space-y-3">
              {warnings.map((warning, index) => (
                <div key={index} className="flex gap-2">
                  <div className="mt-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 font-medium text-primary text-xs">
                    {index + 1}
                  </div>
                  <input
                    type="text"
                    placeholder="ຂໍ້ຄວນລະວັງ..."
                    value={warning}
                    onChange={(e) => handleUpdateWarning(index, e.target.value)}
                    className="flex-1 rounded-md border px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteWarning(index)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddWarning}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            ເພີ່ມຂໍ້ຄວນລະວັງ
          </Button>
        </div>
      </div>
    </div>
  );
}
